/**
 * SETUP — categories, the address category, tags, the language and the publication wish.
 *
 * ★★ ONE CATEGORY PER ARTICLE (design 24, T4: the owner, 2026-10-11, "we don't need multiple
 * categories; for that we have tags"). `WorkingCopy.categories` is still `string[]` and the limit
 * is still a policy value checked at submit, but this pane always draws ONE choice, whatever the
 * policy says: picking a category replaces the one before. **The list is served** by
 * `listEditorCategories` from ADMIN-managed reference data (`docs/ui/04-pages.md:458`); a list
 * baked into a client goes stale, and the codes are not all lower-case, so nothing here matches one.
 *
 * ★★ AND THAT ONE IS THE ARTICLE'S PERMANENT ADDRESS. `addressCategory` is *"which of
 * `categories` becomes half of the article's permanent web address"* - required at submit and
 * **frozen at first publication** (decision-log row 161, question 8(a)). With one category there is
 * nothing to choose, so the separate "Which one goes in the web address" select is gone (T5) and
 * picking a category sets the address category to it. An old draft filed under several shows them
 * all picked; picking one keeps only that one. `chooseSoleAddressCategory` (./address.ts, applied by
 * ./use-editor-draft.ts) still fills the pick in for a copy opened with one category and none
 * chosen (design 14, F8).
 *
 * ★ FORMAT AND TIME SENSITIVITY ARE **NOT DRAWN** (R-004). Both store "a code from the managed
 * lookup", NO SERVED READ LISTS EITHER (served-index.py: the `reference` operations are categories,
 * tags, rules and languages) and both lookups are empty at launch - so the free-text boxes here sent
 * codes the database refused, and every later save failed. A loaded value goes back unchanged.
 * OWED: a reference read, then two Selects. The language, whose set IS served, is drawn below.
 *
 * ★ THE MOON PHASE FOR THE CHOSEN DATE IS **NOT DRAWN**. `docs/ui/04-pages.md:461` marks it
 * PLATFORM WORK OWED: the calendar returns events, not an arbitrary-date phase computation, and
 * the prototype computes it in the browser. A screen that computed it here would be asserting an
 * astronomical fact this platform has not agreed to.
 *
 * ★ AND THE DATE IS WHEN IT GOES LIVE ONCE APPROVED (design 24, T29). `plannedGoLiveAt` is the
 * moment an approval schedules (`ai_schedule`, review-queue.writer.ts); none means at once, and an
 * editor may move it. A date and time picker on the author's clock, from their own today (below).
 *
 * DESIGN 15 (the owner's live look at real cabinets, 2026-10-01): the pane is the editor's "Setup"
 * tab now, under the prototype's "Article setup" title, and says nothing about how the platform
 * works inside - no "catalogue, not this app", no "the platform's calendar"; the offered days read
 * as days ("12 Oct 2026"), not as `2026-10-12`. What it reads and writes is unchanged.
 */
import { Checkbox, Chip, Group, Select, Stack, TagsInput, Text, Title, isOptionsGroup } from '@mantine/core';
import type { ComboboxData, ComboboxParsedItem, OptionsFilter } from '@mantine/core';
import { useState } from 'react';
import { DateTimeField, instantToWall, isKnownTimeZone } from '@moonx/ui';
import { useEditorApi } from '../../../features/cabinet/api/editor-api';
import { formatDay } from '../../../features/cabinet/format';
import type { EditorCategory } from '../../../features/cabinet/api/editor-api';
import type { PublishDateSuggestion } from '../../../features/cabinet/api/editor-api';
import { useAsync } from '../../../features/cabinet/state';
import { useTranslation } from '../../../i18n';
import { usePlatformMonth } from '../../calendar/platform-month';
import type { ArticlesT } from '../articles-i18n';
import { sourceLanguageLabel, sourceLanguageOptions } from './document';
import { usePieceContext } from './piece-context';
import type { EditorDraft, OfferedLanguage } from './document';

export interface SetupPanelProps {
  readonly draft: EditorDraft;
  readonly disabled: boolean;
  readonly categories: readonly EditorCategory[];
  /** Other authors' tags: asked with nothing typed, then again with what is typed (T6). */
  readonly tagSuggestions: readonly string[];
  /** MoonX's own tags, grouped and ordered as the server sends them; none before the pause. */
  readonly tagGroups: readonly TagGroup[];
  /** Called with the typed text on every change of the tag field; the editor asks again with it. */
  readonly onTagSearch: (query: string) => void;
  readonly publishDates: readonly PublishDateSuggestion[];
  /** Ignored since design 24, T4: Setup draws one choice, whatever the policy's maximum. */
  readonly maxCategories?: number | null | undefined;
  readonly onChange: (change: (draft: EditorDraft) => EditorDraft) => void;
  /** A version exists or the piece has left `draft`: the platform refuses a language change now. */
  readonly languageFixed: boolean;
  /** The platform refused a language change on this visit, and the autosave put the pick back. */
  readonly languageRefused: boolean;
  /** The author's IANA zone for the publication time (T29); absent, the browser's own. */
  readonly timeZone?: string | undefined;
  /** The pane's title and line; absent, the article's. A video says its own (v1.0.29). */
  readonly title?: string | undefined;
  readonly intro?: string | undefined;
}

export function SetupPanel({
  draft,
  disabled,
  categories,
  tagSuggestions,
  tagGroups,
  onTagSearch,
  publishDates,
  onChange,
  languageFixed,
  languageRefused,
  timeZone,
  title,
  intro,
}: SetupPanelProps) {
  const { t } = useTranslation('articles');
  const context = usePieceContext();
  const languages = useOfferedLanguages();
  const [tagSearch, setTagSearch] = useState('');
  const serverToday = useServerToday(timeZone); // the server's day, on the author's clock (T29)
  // ★ ONE CHOICE, IT IS THE ADDRESS (T4/T5): set here - the sole-category rule keeps an old pick.
  const pickCategory = (code: string) => {
    onChange((current) => ({ ...current, categories: [code], addressCategory: code }));
  };

  return (
    <Stack component="section" gap="md" aria-labelledby="editor-setup-title">
      {/* The prototype's title and line (design 15). The tab over this pane is "Setup". */}
      <Stack gap={4}>
        <Title id="editor-setup-title" order={2} size="h5">
          {title ?? t('editor.setup.title')}
        </Title>
        <Text size="xs" c="dimmed">
          {intro ?? t('editor.setup.intro')}
        </Text>
      </Stack>

      <Stack gap={6}>
        <Text size="sm" fw={700} component="span" id="editor-categories-label">
          {t('editor.setup.category')}
        </Text>
        <Text size="xs" c="dimmed">
          {t('editor.setup.pickOne')}
        </Text>
        <Group gap="xs" wrap="wrap" role="group" aria-labelledby="editor-categories-label">
          {categories.map((category) => {
            const checked = draft.categories.includes(category.code);
            return (
              <Chip
                key={category.code}
                checked={checked}
                disabled={disabled}
                variant="light"
                size="sm"
                onChange={() => {
                  pickCategory(category.code);
                }}
              >
                {category.label}
              </Chip>
            );
          })}
        </Group>
      </Stack>

      {/* "Which one goes in the web address" is gone (design 24, T5). It was the author's pick of
          which category named the address when there were several; with one category per article
          that one is the address, set by the pick above, and frozen at first publication as
          before. */}

      {/* ★ THE TAGS (design 24, T6). The list: MoonX's own, grouped and ordered as the server sends
          it, then other authors' tags under "Used by other authors" - each tag once, none already
          added (`tagOptions`, below). Typing narrows every group (`keepMatchingGroups`) and asks
          the server again with what is typed (`onTagSearch`, debounced by the editor), so other
          authors' tags beyond the first twenty appear too. Any other tag can still be typed in;
          before the pause applies the list, the server sends no groups and the field is as it was. */}
      <TagsInput
        label={t('editor.setup.tags')}
        description={t('editor.setup.tagsNote')}
        disabled={disabled}
        value={[...draft.tags]}
        data={tagOptions(tagGroups, tagSuggestions, draft.tags, t)}
        filter={keepMatchingGroups}
        searchValue={tagSearch}
        onSearchChange={(query) => {
          setTagSearch(query);
          onTagSearch(query);
        }}
        onChange={(tags) => {
          onChange((current) => ({ ...current, tags }));
        }}
      />

      {/* ★ THE LANGUAGE (R-003): the set the platform serves (`listSourceLanguages`, wave 26), sent
          only when it changes, fixed once a version exists. A refused change is put back by the
          autosave (./use-editor-draft.ts), and `languageRefused` is how this pane learns it. */}
      {context === 'video' ? null : (
        <Checkbox
          label={t('editor.setup.writtenWithAi')}
          description={t('editor.setup.writtenWithAiNote')}
          disabled={disabled}
          checked={draft.writtenWithAi}
          onChange={(event) => {
            const writtenWithAi = event.currentTarget.checked;
            onChange((current) => ({ ...current, writtenWithAi }));
          }}
        />
      )}

      <Stack gap={6}>
        <Select
          label={t('editor.setup.language', { context })}
          description={
            languageFixed || languageRefused
              ? t('editor.setup.languageFixed', { context })
              : t('editor.setup.languageNote', { context })
          }
          disabled={disabled || languageFixed || languageRefused}
          allowDeselect={false}
          value={draft.sourceLanguage}
          data={[...sourceLanguageOptions(draft.sourceLanguage, languages.offered, t)]}
          onChange={(value) => {
            if (value !== null) {
              onChange((current) => ({ ...current, sourceLanguage: value }));
            }
          }}
        />
        {languageRefused ? (
          <Text size="xs" role="alert">
            {t('editor.setup.languageRefused', {
              language: sourceLanguageLabel(draft.sourceLanguage, languages.offered, t),
            })}
          </Text>
        ) : null}
        {languages.failed ? (
          <Text size="xs" c="dimmed">
            {t('editor.setup.languagesFailed', { context })}
          </Text>
        ) : null}
      </Stack>

      {/* ★ THE PUBLICATION DATE AND TIME (design 24, T29, the header): any day from the server's
          today, any time, on the author's clock; the calendar's days are quick picks under it. */}
      <DateTimeField
        label={t('editor.setup.publication')}
        description={t('editor.setup.publicationNote', { context })}
        placeholder={t('editor.setup.publicationPlaceholder')}
        disabled={disabled}
        value={draft.plannedGoLiveAt}
        onChange={(plannedGoLiveAt) => {
          onChange((current) => ({ ...current, plannedGoLiveAt }));
        }}
        // Not a clock read: the zone the browser is set to, when the author has named none.
        timeZone={timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
        minDay={serverToday}
        openingDay={serverToday}
        quickPicks={publishDates.map((suggestion) => ({
          day: suggestion.date,
          label: `${formatDay(suggestion.date)} — ${suggestion.reason}`,
        }))}
        quickPicksLabel={t('editor.setup.suggested')}
      />
    </Stack>
  );
}

/**
 * ★ THE FIRST DAY THAT CAN BE PICKED (design 24, T29): the server's own instant (`computedAt`,
 * ../../calendar/platform-month.ts - never the browser's clock, C11) as a day on the AUTHOR'S
 * clock, which is the clock the field is on. Its UTC day would refuse an author's own evening west
 * of UTC and offer their yesterday east of it. The zone is the field's: the one named, else the
 * browser's, and one the browser does not know is UTC, as `DateTimeField` reads it.
 */
function useServerToday(timeZone: string | undefined): string | undefined {
  const instant = usePlatformMonth(undefined, 'ready').instant;
  // Not a clock read: the zone the browser is set to, when the author has named none.
  const named = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const zone = isKnownTimeZone(named) ? named : 'UTC';
  return instant === undefined ? undefined : instantToWall(instant, zone)?.slice(0, 10);
}

/** One group of MoonX's own tags, as `suggestTags` answers it with nothing typed (design 24, T6). */
export interface TagGroup {
  readonly label: string;
  readonly tags: readonly string[];
}

/** Two spellings of one tag share this key - NFC, trimmed, lower-cased - as the server groups them. */
function tagKey(tag: string): string {
  return tag.normalize('NFC').trim().toLowerCase();
}

/**
 * ★ THE FIELD'S OPTIONS (design 24, T6). MoonX's list in the server's groups and order, then the
 * other authors' tags that are not on it, under "Used by other authors". Each tag is offered once,
 * in its first spelling - the list's "Full moon", not another author's "full moon" - and a tag
 * already added is not offered at all. With no list (the server before the pause applies it) the
 * other authors' tags are offered alone and flat, as before.
 */
export function tagOptions(
  groups: readonly TagGroup[],
  others: readonly string[],
  added: readonly string[],
  t: ArticlesT,
): ComboboxData {
  const seen = new Set(added.map(tagKey));
  const once = (tags: readonly string[]): string[] =>
    tags.filter((tag) => {
      const key = tagKey(tag);
      if (key.length === 0 || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  const listed = groups
    .map((group) => ({ group: group.label, items: once(group.tags) }))
    .filter((group) => group.items.length > 0);
  const rest = once(others);
  if (groups.length === 0) {
    return rest;
  }
  // Where other authors' tags go once MoonX's list is there.
  return rest.length === 0
    ? listed
    : [...listed, { group: t('editor.setup.otherAuthors'), items: rest }];
}

/** Typing narrows every group; a group left with nothing is not drawn as an empty heading. */
const keepMatchingGroups: OptionsFilter = ({ options, search }) => {
  const typed = search.trim().toLowerCase();
  const matches = (label: string): boolean => label.toLowerCase().includes(typed);
  return options.flatMap((option): ComboboxParsedItem[] => {
    if (!isOptionsGroup(option)) {
      return matches(option.label) ? [option] : [];
    }
    const items = option.items.filter((item) => matches(item.label));
    return items.length === 0 ? [] : [{ ...option, items }];
  });
};

/**
 * THE SERVED SET, READ BY THIS PANE (wave 26). `listSourceLanguages` answers the languages the
 * translation policy in force offers - the set the autosave checks a change against - so the picker
 * offers what a save accepts. While it loads, or when it fails, `offered` is empty and the picker
 * holds only the stored language (./document.ts, `sourceLanguageOptions`): a list this app made up
 * in the meantime would be the hard-coded copy the read replaced. A failure is said, below the pick.
 */
function useOfferedLanguages(): {
  readonly offered: readonly OfferedLanguage[];
  readonly failed: boolean;
} {
  const api = useEditorApi();
  const read = useAsync((signal) => api.listSourceLanguages(signal), 'editor:source-languages');
  return { offered: read.data?.items ?? [], failed: read.status === 'error' };
}
